import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.error('❌ Falta REACT_APP_SUPABASE_URL o REACT_APP_SUPABASE_ANON_KEY en .env.local');
}

export const supabase = createClient(supabaseUrl, supabaseKey);

// Helper: obtener user_id actual
export async function getUserId() {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id;
}

// ─── PERFILES USUARIO ──────────────────────────────────
export const perfilService = {
  async obtener() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from("perfiles_usuario")
      .select("*")
      .eq("id", user.id)
      .single();

    return data || null;
  },

  async crear(perfil) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("No hay usuario autenticado");

    // upsert: crea la fila o la actualiza si ya existía (registro a medias)
    const { data, error } = await supabase
      .from("perfiles_usuario")
      .upsert([{ id: user.id, ...perfil }], { onConflict: "id" })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async actualizar(updates) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("No hay usuario");

    const { data, error } = await supabase
      .from("perfiles_usuario")
      .update(updates)
      .eq("id", user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

// ─── PRECIOS ACG (Edge Function) ──────────────────────
export const preciosService = {
  async obtener() {
    const { data, error } = await supabase.functions.invoke("precios-acg");
    if (error) {
      console.warn("No se pudieron obtener precios ACG:", error.message);
      return null;
    }
    return data; // { fecha, fuente, live, items:[...] }
  },
};

// ─── ASESOR IA (Edge Function) ────────────────────────
export const iaService = {
  async generar({ resumen, totalAnim, nPotreros }) {
    const { data, error } = await supabase.functions.invoke("asesor-ia", {
      body: { resumen, totalAnim, nPotreros },
    });
    if (error) throw new Error(error.message || "No se pudo contactar al asesor");
    if (data?.error) throw new Error(data.error);
    return data?.text || "Sin respuesta.";
  },
};

// ─── PRECIOS SEMILLAS ANAPROSE (Edge Function) ────────
export const semillasService = {
  async obtener() {
    const { data, error } = await supabase.functions.invoke("precios-semillas");
    if (error) {
      console.warn("No se pudieron obtener precios de semillas:", error.message);
      return null;
    }
    return data;
  },
};

// ─── MERCADO: PUBLICACIONES (tabla compartida) ────────
export const publicacionService = {
  // Todos los avisos activos de todos los usuarios
  async mercado() {
    const { data, error } = await supabase
      .from("publicaciones").select("*")
      .eq("estado", "activa").order("created_at", { ascending: false });
    if (error) { console.error("Error mercado:", error.message); return []; }
    return data || [];
  },
  // Mis avisos (activos y vendidos)
  async mios() {
    const userId = await getUserId();
    if (!userId) return [];
    const { data, error } = await supabase
      .from("publicaciones").select("*")
      .eq("user_id", userId).order("created_at", { ascending: false });
    if (error) { console.error(error.message); return []; }
    return data || [];
  },
  async crear(pub) {
    const userId = await getUserId();
    if (!userId) throw new Error("No hay usuario");
    const { data, error } = await supabase
      .from("publicaciones").insert([{ ...pub, user_id: userId }]).select().single();
    if (error) throw error;
    return data;
  },
  async actualizar(id, updates) {
    const { error } = await supabase.from("publicaciones").update(updates).eq("id", id);
    if (error) throw error;
  },
  async eliminar(id) {
    const { error } = await supabase.from("publicaciones").delete().eq("id", id);
    if (error) throw error;
  },
};

// ─── MERCADO: CONTACTOS / INTERESADOS ─────────────────
export const contactoService = {
  async enviar(c) {
    const userId = await getUserId();
    if (!userId) throw new Error("No hay usuario");
    const { error } = await supabase.from("contactos").insert([{ ...c, de_user_id: userId }]);
    if (error) throw error;
  },
  // Contactos recibidos en mis avisos
  async recibidos() {
    const userId = await getUserId();
    if (!userId) return [];
    const { data, error } = await supabase
      .from("contactos").select("*")
      .eq("para_user_id", userId).order("created_at", { ascending: false });
    if (error) { console.error(error.message); return []; }
    return data || [];
  },
  async marcarLeido(id) {
    const { error } = await supabase.from("contactos").update({ leido: true }).eq("id", id);
    if (error) throw error;
  },
};

// ─── ESTADO COMPLETO (documento por usuario) ──────────
// Guarda TODO el estado de la app como un único documento JSON por usuario.
// Es el modelo que mejor calza con cómo funciona la app (estado en memoria,
// animales anidados en potreros) y evita errores de mapeo de columnas.
export const estadoService = {
  async cargar() {
    const userId = await getUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from("app_state")
      .select("data")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("Error cargando estado:", error);
      return null;
    }
    return data?.data || null;
  },

  async guardar(estado) {
    const userId = await getUserId();
    if (!userId) throw new Error("No hay usuario");

    const { error } = await supabase
      .from("app_state")
      .upsert(
        [{ user_id: userId, data: estado, updated_at: new Date().toISOString() }],
        { onConflict: "user_id" }
      );

    if (error) {
      console.error("Error guardando estado:", error);
      throw error;
    }
  },
};

// ─── POTREROS ─────────────────────────────────────────
export const potreroService = {
  async getAll() {
    const userId = await getUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from('potreros')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching potreros:', error);
      return [];
    }
    return data || [];
  },

  async create(potrero) {
    const userId = await getUserId();
    if (!userId) throw new Error("No hay usuario");

    const { data, error } = await supabase
      .from('potreros')
      .insert([{ ...potrero, user_id: userId }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from('potreros')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('potreros')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── ANIMALES ──────────────────────────────────────────
export const animalService = {
  async getByPotrero(potreroId) {
    const userId = await getUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from('animales')
      .select('*')
      .eq('user_id', userId)
      .eq('potrero_id', potreroId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching animales:', error);
      return [];
    }
    return data || [];
  },

  async create(animal) {
    const userId = await getUserId();
    if (!userId) throw new Error("No hay usuario");

    const { data, error } = await supabase
      .from('animales')
      .insert([{ ...animal, user_id: userId }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from('animales')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('animales')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── STOCK SANIDAD ────────────────────────────────────
export const stockService = {
  async getAll() {
    const userId = await getUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from('stock_sanidad')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching stock:', error);
      return [];
    }
    return data || [];
  },

  async create(item) {
    const userId = await getUserId();
    if (!userId) throw new Error("No hay usuario");

    const { data, error } = await supabase
      .from('stock_sanidad')
      .insert([{ ...item, user_id: userId }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from('stock_sanidad')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('stock_sanidad')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── TRANSACCIONES ────────────────────────────────────
export const transaccionService = {
  async getAll() {
    const userId = await getUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from('transacciones')
      .select('*')
      .eq('user_id', userId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching transacciones:', error);
      return [];
    }
    return data || [];
  },

  async create(transaccion) {
    const userId = await getUserId();
    if (!userId) throw new Error("No hay usuario");

    const { data, error } = await supabase
      .from('transacciones')
      .insert([{ ...transaccion, user_id: userId }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('transacciones')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── FARDOS ───────────────────────────────────────────
export const fardoService = {
  async getAll() {
    const userId = await getUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from('fardos')
      .select('*')
      .eq('user_id', userId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching fardos:', error);
      return [];
    }
    return data || [];
  },

  async create(fardo) {
    const userId = await getUserId();
    if (!userId) throw new Error("No hay usuario");

    const { data, error } = await supabase
      .from('fardos')
      .insert([{ ...fardo, user_id: userId }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from('fardos')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('fardos')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};
